import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  MenuItem,
  Stack,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";

const API_URL = process.env.REACT_APP_API_URL;

const EditPost = () => {
  const { postId } = useParams();
  const { userId } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    duration: "",
    selectedDate: "",
    startTime: "",
    media_key: "",
    language: "",
  });

  const [originalPost, setOriginalPost] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload file to S3
  const uploadToS3 = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(`${API_URL}/api/upload-s3/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.key;
  };

  // Fetch post details
  useEffect(() => {
    if (!postId || !userId) return;

    axios
      .post(`${API_URL}/posts/detail/`, { postId, userId })
      .then((res) => {
        setForm(res.data);
        setOriginalPost(res.data);
        setMediaPreview(res.data.media_url);
      })
      .catch((err) => {
        console.error(err);
        alert(err.response?.data?.error || "Failed to load post");
      });
  }, [postId, userId]);

  // Fetch languages
  useEffect(() => {
    axios
      .get(`${API_URL}/list_languages/`)
      .then((res) => setLanguages(res.data.filter((lang) => lang.is_active)))
      .catch((err) => console.error(err));
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  // Submit edited post
  const handleSubmit = async () => {
    if (!postId || !userId || !originalPost) return;

    setLoading(true);

    try {
      // Use existing media_key if no new file is uploaded
      let media_key = form.media_key || originalPost.media_key;
      if (newFile) media_key = await uploadToS3(newFile);

      // Ensure startTime is HH:MM format (remove seconds if any)
      let startTimeFormatted = form.startTime || originalPost.startTime;
      if (startTimeFormatted && startTimeFormatted.length > 5) {
        startTimeFormatted = startTimeFormatted.slice(0, 5);
      }

      // Merge with original post for partial updates
      const payload = {
        postId,
        userId,
        title: form.title || originalPost.title,
        description: form.description || originalPost.description,
        price: form.price || originalPost.price,
        duration: form.duration || originalPost.duration,
        selectedDate: form.selectedDate || originalPost.selectedDate,
        startTime: startTimeFormatted || originalPost.startTime,
        media_key,
        language: form.language || originalPost.language,
      };

      await axios.put(`${API_URL}/posts/update/`, payload);
      alert("Post updated successfully!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!postId || !userId || !originalPost) return <p>Loading...</p>;

  return (
    <div>
      <Header1 />
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 5 }}>
          <Typography variant="h3" fontWeight="bold" mb={3} textAlign="center">
            Edit Post
          </Typography>

          {/* Title */}
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={form.title}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            name="description"
            multiline
            rows={4}
            value={form.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {/* Language */}
          <TextField
            select
            fullWidth
            label="Language"
            name="language"
            value={form.language || ""}
            onChange={handleChange}
            sx={{ mb: 2 }}
          >
            {languages.map((lang) => (
              <MenuItem key={lang.id} value={lang.name}>
                {lang.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Price */}
          <TextField
            fullWidth
            label="Price"
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {/* Duration */}
          <TextField
            fullWidth
            label="Duration (hours)"
            name="duration"
            value={form.duration}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {/* Date */}
          <TextField
            fullWidth
            type="date"
            label="Date"
            name="selectedDate"
            InputLabelProps={{ shrink: true }}
            value={form.selectedDate || ""}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          {/* Start Time */}
          <TextField
            fullWidth
            type="time"
            label="Start Time"
            name="startTime"
            InputLabelProps={{ shrink: true }}
            value={form.startTime || ""}
            onChange={handleChange}
            sx={{ mb: 3 }}
          />

          {/* Media Preview */}
          {mediaPreview && (
            <Box mb={2}>
              <Typography variant="subtitle2">Current Media</Typography>
              <img
                src={mediaPreview}
                alt="media"
                style={{ width: "100%", borderRadius: 8 }}
              />
            </Box>
          )}

          {/* Replace Media */}
<Stack spacing={1} sx={{ mb: 3 }}>
  <Button
    variant="outlined"
    component="label"
    fullWidth
  >
    Replace Media
    <input
      hidden
      type="file"
      accept="image/*,video/*"
      onChange={handleFileChange}
    />
  </Button>

  <Typography
    variant="caption"
    sx={{ color: "rgba(255,255,255,0.8)" }}
  >
    Max file size: 1 MB (Image / Video accepted)
  </Typography>
</Stack>


          {/* Save Changes */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </Paper>
      </Container>
      <Footer2 />
    </div>
  );
};

export default EditPost;
